/** @jsx h */
import { App, component, h } from 'literaljs';
import { Router } from 'literaljs-router';
import './index.scss';
import './learn-styles.scss';
import { initTheme } from './utils/theme';

// Initialize theme before app mounts
initTheme();

// Components
import Navbar from './components/Navbar';
import HomePage from './components/Pages/HomePage';
import DocsPage from './components/Pages/DocsPage';
import ExamplesPage from './components/Pages/ExamplesPage';
import LearnPage from './components/Pages/LearnPage';
import ChangelogPage from './components/Pages/ChangelogPage';

// Route definitions
const routes = [
	{ path: '/', render: () => h(HomePage) },
	{ path: '/docs', render: () => h(DocsPage) },
	{ path: '/examples', render: () => h(ExamplesPage) },
	{ path: '/learn', render: () => h(LearnPage) },
	{ path: '/changelog', render: () => h(ChangelogPage) },
	{ path: '/404', render: () => h('div', { class: 'not-found-page' }, [
		h('h1', {}, '404'),
		h('p', {}, 'Page not found'),
		h('a', { href: '/', style: { color: 'var(--neon-cyan)' } }, 'Go home')
	]) }
];

const Page = component({
	name: 'Page',
	state: { isTransitioning: false, transitionDirection: 'in', showShortcutsModal: false },
	mounted() {
		this._onRouteChange = () => {
			this.setState({ isTransitioning: true });
			setTimeout(() => {
				this.setState({ isTransitioning: false });
			}, 300);
		};
		window.addEventListener('literaljs-route', this._onRouteChange);

		this._onKeyDown = (e) => {
			const target = e.target;
			const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

			if (e.key === '?' && !isInput) {
				e.preventDefault();
				this.setState({ showShortcutsModal: true });
			}

			if (e.key === 'Escape' && this.getState().showShortcutsModal) {
				this.setState({ showShortcutsModal: false });
			}

			// Cmd+K / Ctrl+K — focus the Learn page search input
			if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K') && !e.shiftKey && !e.altKey) {
				e.preventDefault();
				const currentPath = window.location.pathname;
				if (currentPath !== '/learn') {
					window.history.pushState({}, '', '/learn');
					window.dispatchEvent(new Event('popstate'));
				}
				setTimeout(() => {
					const searchInput = document.querySelector('.api-toolbar input[type="text"], .api-toolbar input[type="search"], .api-toolbar .search-bar input');
					if (searchInput) {
						searchInput.focus();
						searchInput.select();
					}
				}, 50);
			}
		};
		document.addEventListener('keydown', this._onKeyDown);
	},
	unmounted() {
		if (this._onRouteChange) {
			window.removeEventListener('literaljs-route', this._onRouteChange);
		}
		if (this._onKeyDown) {
			document.removeEventListener('keydown', this._onKeyDown);
		}
	},
	render() {
		const { isTransitioning, showShortcutsModal } = this.getState();
		const transitionClass = isTransitioning ? 'page-transitioning' : '';

		const shortcuts = [
			{ label: 'Show shortcuts', keys: ['?'] },
			{ label: 'Close modals/menus', keys: ['Esc'] },
			{ label: 'Search (on Learn page)', keys: ['Cmd', 'K'] },
			{ label: 'Navigate focus', keys: ['Tab'] },
			{ label: 'Activate focused element', keys: ['Enter'] },
			{ label: 'Navigate search results', keys: ['↑', '↓'] }
		];

		return (
			<div>
				<Navbar />
				<main id="main-content" class={`main-content ${transitionClass}`}>
					{Router({ routes })}
				</main>
				<footer class="site-footer site-footer-shared">
					<div class="footer-content">
						<div class="footer-bottom">
							<span>LiteralJS v8.1.11 — MIT License</span>
							<button
								class="footer-shortcuts-btn"
								onClick={() => this.setState({ showShortcutsModal: true })}
								aria-label="Open keyboard shortcuts"
							>
								<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
									<rect x="2" y="4" width="20" height="16" rx="2" />
									<path d="M6 8h.01" />
									<path d="M10 8h.01" />
									<path d="M14 8h.01" />
									<path d="M18 8h.01" />
									<path d="M8 12h.01" />
									<path d="M12 12h.01" />
									<path d="M16 12h.01" />
									<path d="M7 16h10" />
								</svg>
								Shortcuts
							</button>
						</div>
					</div>
				</footer>
				{showShortcutsModal && (
					<div
						class="shortcuts-modal-overlay"
						role="dialog"
						aria-modal="true"
						aria-label="Keyboard shortcuts"
						onClick={() => this.setState({ showShortcutsModal: false })}
					>
						<div
							class="shortcuts-modal"
							onClick={(e) => e.stopPropagation()}
						>
							<div class="shortcuts-modal-header">
								<h2 class="shortcuts-modal-title">
									<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
										<rect x="2" y="4" width="20" height="16" rx="2" />
										<path d="M6 8h.01" />
										<path d="M10 8h.01" />
										<path d="M14 8h.01" />
										<path d="M18 8h.01" />
										<path d="M8 12h.01" />
										<path d="M12 12h.01" />
										<path d="M16 12h.01" />
										<path d="M7 16h10" />
									</svg>
									Keyboard Shortcuts
								</h2>
								<button
									class="shortcuts-modal-close"
									onClick={() => this.setState({ showShortcutsModal: false })}
									aria-label="Close shortcuts modal"
								>
									<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
										<line x1="18" y1="6" x2="6" y2="18" />
										<line x1="6" y1="6" x2="18" y2="18" />
									</svg>
								</button>
							</div>
							<div class="shortcuts-list">
								{shortcuts.map((shortcut) => (
									<div class="shortcut-item" key={shortcut.label}>
										<span class="shortcut-label">{shortcut.label}</span>
										<span class="shortcut-keys">
											{shortcut.keys.map((key, i) => (
												<span key={key}>
													<span class="shortcut-key">{key}</span>
													{i < shortcut.keys.length - 1 && <span style={{ color: 'var(--text-muted)', margin: '0 0.25rem' }}>+</span>}
												</span>
											))}
										</span>
									</div>
								))}
							</div>
						</div>
					</div>
				)}
			</div>
		);
	}
});

// Initial store
const store = { showMenu: false };

// Mount the app
const app = new App(Page, store);

// Listen for route changes from the router and trigger re-render
window.addEventListener('literaljs-route', () => {
	app.scheduleUpdate();
});

app.mount('root');

// Debug: expose app for diagnostics
